import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ArrowLeftRight,
  Compass,
  GitCompareArrows,
  List,
  X,
} from 'lucide-react'

import { DRAWER_CONTENT_PADDING_X, MOBILE_DRAWER_WIDTH } from '@/config/layoutBreakpoints'
import { buildMobileDrawerSections, isPrimaryNavActive } from '@/config/navigation'
import { BridgeSiteLink } from '@/components/BridgeSiteLink'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { isInternalAllowlistedAccount } from '@/constants/internalAllowlist'
import { useAppWeb3 } from '@/hooks'
import { cn } from '@/lib/utils'
import { MobileTokenSearchButton } from '@/mobile'
import { useMobileTokenSearch } from '@/mobile/token-picker'

import { WalletSessionPanel } from './WalletSessionPanel'

const mobileQuickActionKeys = [
  { titleKey: 'swap' as const, href: '/swap', icon: ArrowLeftRight, external: false },
  { titleKey: 'explore' as const, href: '/explore', icon: Compass, external: false },
  { titleKey: 'positions' as const, href: '/pool', icon: List, external: false },
  { titleKey: 'bridge' as const, href: '', icon: GitCompareArrows, external: true },
] as const

export function AppSidebar({
  mobileOpen: externalMobileOpen,
  onClose: externalOnClose,
}: {
  mobileOpen?: boolean
  onClose?: () => void
} = {}) {
  const location = useLocation()
  const t = useTranslation()
  const mobileDrawerSections = buildMobileDrawerSections(t)
  const { account } = useAppWeb3()
  const { openTokenSearch } = useMobileTokenSearch()
  const [internalMobileOpen, setInternalMobileOpen] = useState(false)

  const mobileOpen = externalMobileOpen !== undefined ? externalMobileOpen : internalMobileOpen
  const setMobileOpen = (value: boolean) => {
    if (externalOnClose && !value) {
      externalOnClose()
    } else {
      setInternalMobileOpen(value)
    }
  }

  const allowlisted = isInternalAllowlistedAccount(account)

  return (
    <>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-[55] bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      ) : null}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-[60] overflow-hidden border-r border-white/10 bg-[var(--mobile-surface,#0f1219)] transition-transform md:hidden',
          MOBILE_DRAWER_WIDTH,
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className={cn('flex shrink-0 items-center justify-end py-2', DRAWER_CONTENT_PADDING_X)}>
            <button
              type="button"
              className="mobile-app__menu-btn"
              onClick={() => setMobileOpen(false)}
              aria-label={t.common.closeMenu}
            >
              <X className="size-5" />
            </button>
          </div>
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]',
              DRAWER_CONTENT_PADDING_X,
              'pb-[max(1rem,env(safe-area-inset-bottom))]',
            )}
            data-mobile-scroll-container
          >
            <WalletSessionPanel />
            <div className="mobile-app__drawer-section">
              <p className="mobile-app__drawer-section-label">{t.shell.quickActions}</p>
              <div className="mobile-app__quick-actions">
                {mobileQuickActionKeys.map((action) => {
                  const Icon = action.icon
                  const title = t.shell.quickAction[action.titleKey]
                  const className = 'mobile-app__quick-action-tile'
                  const content = (
                    <>
                      <Icon className="size-4 shrink-0 text-primary" aria-hidden />
                      <span className="mobile-app__quick-action-tile-label">{title}</span>
                    </>
                  )
                  if (action.external) {
                    return (
                      <BridgeSiteLink
                        key={action.titleKey}
                        className={className}
                        onClick={() => setMobileOpen(false)}
                      >
                        {content}
                      </BridgeSiteLink>
                    )
                  }
                  return (
                    <Link
                      key={action.titleKey}
                      to={action.href}
                      onClick={() => setMobileOpen(false)}
                      className={className}
                    >
                      {content}
                    </Link>
                  )
                })}
                <MobileTokenSearchButton
                  variant="tile"
                  onClick={() => {
                    setMobileOpen(false)
                    openTokenSearch()
                  }}
                />
              </div>
            </div>
            {mobileDrawerSections.map((section) => {
              const items = section.items.filter((item) => !item.internal || allowlisted)
              if (items.length === 0) return null
              return (
                <div key={section.id} className="mobile-app__drawer-section">
                  <p className="mobile-app__drawer-section-label">{section.label}</p>
                  <nav className="mobile-app__list-stack">
                    {items.map((item) => {
                      const Icon = item.icon
                      const active = isPrimaryNavActive(location.pathname, item.href)
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className={cn('mobile-app__list-row w-full', active && 'text-primary')}
                        >
                          <Icon className="size-4 shrink-0" />
                          <span className="mobile-app__list-row-copy">
                            <span className="mobile-app__list-row-title">{item.title}</span>
                          </span>
                          <span className="text-muted-foreground">›</span>
                        </Link>
                      )
                    })}
                  </nav>
                </div>
              )
            })}
          </div>
        </div>
      </aside>
    </>
  )
}
